import { setCookie, deleteCookie, getCookie } from "@huub-web/utils";

import type { FunnelData } from "../types";

/**
 * Default cookie name for storing funnel data.
 */
const DEFAULT_COOKIE_NAME = "funnel";

/**
 * Cookie max age in seconds (7 days).
 * Matches auth cookie expiration.
 */
const COOKIE_MAX_AGE = 7 * 24 * 60 * 60;

export type UseFunnelCookieOptions = {
  /** Cookie name (environment-specific, e.g., huub_funnel_testing) */
  cookieName?: string;
  /** Cookie domain for cross-subdomain sharing (e.g., .huub.nl) */
  cookieDomain?: string;
};

/**
 * Get cookie configuration based on options.
 * - Production: domain=.huub.nl, secure=true
 * - Localhost: no domain (defaults to current host), secure=false
 */
const getCookieConfig = (domain?: string) => {
  const isSecure = !!domain;

  return {
    domain: domain || undefined,
    path: "/",
    secure: isSecure,
    sameSite: "lax" as const,
    maxAge: COOKIE_MAX_AGE,
  };
};

/**
 * Hook for managing funnel data in a cross-domain cookie.
 * Used to persist funnel context across auth flows (email verification, social login).
 *
 * @param options - Cookie configuration options
 * @param options.cookieName - Environment-specific cookie name (default: huub_funnel)
 * @param options.cookieDomain - Domain for cross-subdomain sharing (e.g., .huub.nl)
 */
export const useFunnelCookie = (options: UseFunnelCookieOptions = {}) => {
  const { cookieName = DEFAULT_COOKIE_NAME, cookieDomain } = options;

  /**
   * Sets the funnel cookie with the funnel data.
   * Cookie is configured for cross-domain sharing between marketing site and app.
   */
  const setFunnelCookie = (data: FunnelData): void => {
    const config = getCookieConfig(cookieDomain);
    const value = JSON.stringify(data);
    setCookie(cookieName, value, config);
  };

  /**
   * Gets the funnel data from the cookie.
   * @returns The funnel data or null if not found/invalid
   */
  const getFunnelCookie = (): FunnelData | null => {
    const value = getCookie(cookieName);

    if (!value) {
      return null;
    }

    try {
      return JSON.parse(value) as FunnelData;
    } catch {
      return null;
    }
  };

  /**
   * Deletes the funnel cookie.
   * Must use same domain/path as when setting.
   */
  const deleteFunnelCookie = (): void => {
    const config = getCookieConfig(cookieDomain);
    deleteCookie(cookieName, {
      domain: config.domain,
      path: config.path,
    });
  };

  return { setFunnelCookie, getFunnelCookie, deleteFunnelCookie };
};
